import { useEffect, useMemo, useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { SiweMessage } from "siwe";
import { useActiveAccount } from "thirdweb/react";
import { signMessage } from "thirdweb/utils";
import { getExpirationDate, validExpiration } from "@/helpers/date_helper";
import { selectUserAddress, selectChainId, selectKycSignature } from "@/store/user/selectors";
import { SET_KYC_SIGNATURE, SET_KYC_STATUS_FROM_IFRAME } from "@/store/user/actionTypes";
import { kycIframe } from "@/config";
import { trackKycStarted, trackKycCompleted } from "@/helpers/analytics_helper";

export function buildKycIframeSrc(baseUrl, mode, language) {
  const modeParam = mode ? `&mode=${mode}` : "";
  return `${baseUrl}/?embed=1&open=1${modeParam}&lng=${language || "en"}`;
}

function KycIframeModal({ show, onHide, mode, userAddress, chainId, kycSignature }) {
  const iframeRef = useRef(null);
  const [heightPx, setHeightPx] = useState(620);
  const dispatch = useDispatch();
  const account = useActiveAccount();
  const { i18n } = useTranslation();

  const authAskedRef = useRef(false);
  const lastTrackedKycResultRef = useRef(null);

  const computedSrc = useMemo(
    () => buildKycIframeSrc(kycIframe.url, mode, i18n.resolvedLanguage),
    [mode, i18n.resolvedLanguage]
  );

  const iframeOrigin = useMemo(() => new URL(computedSrc, window.location.href).origin, [computedSrc]);
  const allowAnyOrigin = kycIframe.allowAnyOrigin;

  const isTrustedEventHost = useCallback(
    (ev) => {
      if (ev?.source !== iframeRef.current?.contentWindow) return false;
      return allowAnyOrigin ? true : ev?.origin === iframeOrigin;
    },
    [allowAnyOrigin, iframeOrigin]
  );

  const postToIframe = useCallback(
    (payload) => {
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      win.postMessage(payload, allowAnyOrigin ? "*" : iframeOrigin);
    },
    [allowAnyOrigin, iframeOrigin]
  );

  const trySendStoredSiwe = useCallback(() => {
    if (!kycSignature) return false;
    if (!validExpiration(kycSignature.expiration)) return false;
    if (kycSignature.address.toLowerCase() !== userAddress.toLowerCase()) return false;
    if (kycSignature.chainId !== chainId) return false;

    postToIframe({
      type: "kyc:siwe",
      address: kycSignature.address,
      chainId: kycSignature.chainId,
      message: kycSignature.message,
      signature: kycSignature.signature,
    });
    return true;
  }, [kycSignature, userAddress, chainId, postToIframe]);

  const requestSiweSignature = useCallback(async () => {
    if (!userAddress || !chainId) return;
    if (authAskedRef.current) return;
    authAskedRef.current = true;

    const expiration = getExpirationDate();
    const msg = new SiweMessage({
      domain: window.location.host,
      address: userAddress,
      statement: "Sign in to Ensuro KYC (via host)",
      uri: window.location.origin,
      version: "1",
      chainId: Number(chainId),
      nonce: window.crypto?.getRandomValues
        ? String(window.crypto.getRandomValues(new Uint32Array(1))[0])
        : String(Math.floor(Math.random() * 1e9)),
      issuedAt: new Date().toISOString(),
      expirationTime: expiration,
    });

    const prepared = msg.prepareMessage();

    try {
      const signature = await signMessage({ account, message: prepared });
      dispatch({
        type: SET_KYC_SIGNATURE,
        kycSignature: {
          address: userAddress,
          chainId: Number(chainId),
          message: prepared,
          signature,
          expiration,
        },
      });
    } catch {
      authAskedRef.current = false;
    }
  }, [dispatch, userAddress, chainId, account]);

  useEffect(() => {
    if (!kycSignature) return;
    if (kycSignature.address.toLowerCase() !== userAddress.toLowerCase()) return;
    if (kycSignature.chainId !== chainId) return;

    if (mode === "notifications") {
      // Send the completed signature as kyc:sign-in so the iframe can call /signin/
      postToIframe({
        type: "kyc:sign-in",
        address: kycSignature.address,
        chainId: kycSignature.chainId,
        message: kycSignature.message,
        signature: kycSignature.signature,
      });
    } else {
      postToIframe({
        type: "kyc:siwe",
        address: kycSignature.address,
        chainId: kycSignature.chainId,
        message: kycSignature.message,
        signature: kycSignature.signature,
      });
    }

    authAskedRef.current = false;
  }, [mode, kycSignature, userAddress, chainId, postToIframe]);

  const onMessage = useCallback(
    (ev) => {
      if (!isTrustedEventHost(ev)) return;

      const data = ev?.data;
      if (!data || typeof data !== "object") return;

      switch (data.type) {
        case "kyc:ready": {
          if (mode === "notifications") {
            // Send address for public polling
            if (userAddress && chainId) {
              postToIframe({ type: "kyc:connect", address: userAddress, chainId });
            }
            // If a valid signature is already cached, send it as kyc:sign-in immediately
            if (
              kycSignature &&
              validExpiration(kycSignature.expiration) &&
              kycSignature.address.toLowerCase() === userAddress?.toLowerCase() &&
              kycSignature.chainId === chainId
            ) {
              postToIframe({
                type: "kyc:sign-in",
                address: kycSignature.address,
                chainId: kycSignature.chainId,
                message: kycSignature.message,
                signature: kycSignature.signature,
              });
            }
          } else {
            const reused = trySendStoredSiwe();
            if (!reused) requestSiweSignature();
          }
          break;
        }

        case "kyc:request-sign-in": {
          // iframe asks host to trigger SIWE signing so it can authenticate
          const reused = trySendStoredSiwe();
          if (!reused) requestSiweSignature();
          break;
        }
        case "kyc:resize": {
          const desired = Number(data.height) || 0;
          const next = Math.min(Math.max(desired, 360), 1400);
          setHeightPx(next);
          break;
        }
        case "ETH_EIP_712_SIGN":
          dispatch({
            type: "ETH_EIP_712_SIGN",
            ...data,
          });
          break;
        case "kyc:auth-token": {
          if (mode === "kyc") trackKycStarted("ensuro_kyc");
          break;
        }
        case "kyc:status-updated": {
          dispatch({ type: SET_KYC_STATUS_FROM_IFRAME, isApproved: data.isApproved });
          // PENDING is the only non-final wallet status.
          const isFinal = data.status && data.status !== "PENDING";
          if (mode === "kyc" && isFinal && lastTrackedKycResultRef.current !== data.status) {
            lastTrackedKycResultRef.current = data.status;
            trackKycCompleted("ensuro_kyc", data.status);
          }
          break;
        }
        case "kyc:close": {
          onHide?.();
          break;
        }
        default:
          break;
      }
    },
    [
      isTrustedEventHost,
      trySendStoredSiwe,
      requestSiweSignature,
      onHide,
      mode,
      userAddress,
      chainId,
      postToIframe,
      kycSignature,
      dispatch,
    ]
  );

  useEffect(() => {
    // For notifications mode: always listen (iframe is always mounted for polling).
    // For other modes: only listen when visible.
    if (mode !== "notifications" && !show) return;
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [show, onMessage, mode]);

  useEffect(() => {
    if (!show) return;
    setHeightPx(620);
    authAskedRef.current = false;
    // In notifications mode: trigger sign-in as soon as the modal opens
    // (only if no valid cached signature — otherwise kycSignature useEffect handles it)
    if (mode === "notifications") {
      const hasCached =
        kycSignature &&
        validExpiration(kycSignature.expiration) &&
        kycSignature.address.toLowerCase() === userAddress?.toLowerCase() &&
        kycSignature.chainId === chainId;
      if (!hasCached) requestSiweSignature();
    }
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const stop = (e) => e.stopPropagation();

  // For non-notifications modes: unmount entirely when hidden (original behavior)
  if (mode !== "notifications" && !show) return null;

  return (
    <div
      onClick={show ? onHide : undefined}
      className="kyc-modal-overlay"
      style={{
        display: show ? "flex" : "none",
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 9999,
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        overflowY: "auto",
      }}
    >
      <style>{`
        @media (max-width: 600px) {
          .kyc-modal-overlay { padding: 0 !important; }
          .kyc-modal-card--kyc {
            width: 100vw !important;
            height: 100dvh !important;
            max-height: 100dvh !important;
            border-radius: 0 !important;
            margin: 0 !important;
          }
        }
      `}</style>
      <div
        onClick={stop}
        className={`kyc-modal-card kyc-modal-card--${mode || "kyc"}`}
        style={{
          width: mode === "link" ? "500px" : mode === "notifications" ? "480px" : "min(920px, 95vw)",
          height: mode === "link" ? "480px" : mode === "notifications" ? "560px" : heightPx,
          margin: "auto",
          background: "#fff",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
          position: "relative",
        }}
      >
        <button
          onClick={onHide}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 32,
            height: 32,
            border: 0,
            borderRadius: 6,
            background: "rgba(0,0,0,0.06)",
            cursor: "pointer",
          }}
        >
          ✕
        </button>
        <iframe
          ref={iframeRef}
          title="Ensuro KYC"
          src={computedSrc}
          style={{ width: "100%", height: "100%", border: 0 }}
          sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          allow="camera"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    </div>
  );
}

KycIframeModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  mode: PropTypes.string,
  userAddress: PropTypes.string,
  chainId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  kycSignature: PropTypes.object,
};

const mapStateToProps = (state) => ({
  userAddress: selectUserAddress(state.UserReducer),
  chainId: selectChainId(state.UserReducer),
  kycSignature: selectKycSignature(state.UserReducer),
});

export default connect(mapStateToProps)(KycIframeModal);
